// app/components/Gallery.tsx
"use client";

import { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";

import "swiper/css";
import "swiper/css/pagination";

const GREEN = "rgb(34 150 83)";

const IMAGES = [
  { id: 1, url: "/1.jpeg" },
  { id: 2, url: "/2.jpeg" },
  { id: 3, url: "/3.jpeg" },
  { id: 4, url: "/4.jpeg" },
  { id: 5, url: "/5.jpeg" },
  { id: 6, url: "/6.jpeg" },
  { id: 7, url: "/7.jpeg" },
  { id: 8, url: "/8.jpeg" },
  
  { id: 9, url: "/patient2.jpeg" },
  { id: 10, url: "/patient1.jpeg" },
  { id: 11, url: "/11.jpeg" },
  { id: 12, url: "/10.jpeg" },
  { id: 13, url: "/13.jpeg" },
  { id: 14, url: "/14.jpeg" },
  { id: 15, url: "/15.jpeg" },
];

const CARD_WIDTH = 420;
const GAP = 32;

export default function Gallery() {
  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { once: true, margin: "-20%" });

  const scrollRef = useRef<HTMLDivElement>(null);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const scrollTo = (idx: number) => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTo({
        left: idx * (CARD_WIDTH + GAP),
        behavior: "smooth",
      });
      setCurrentIndex(idx);
    }
  };

  const prev = () => {
    const newIndex =
      currentIndex > 0 ? currentIndex - 1 : IMAGES.length - 1;
    scrollTo(newIndex);
  };

  const next = () => {
    const newIndex =
      currentIndex < IMAGES.length - 1 ? currentIndex + 1 : 0;
    scrollTo(newIndex);
  };

  useEffect(() => {
    const interval = setInterval(next, 3000);
    return () => clearInterval(interval);
  }, [currentIndex]);

  return (
    <section
      id="gallery"
      ref={rootRef}
      className="relative overflow-hidden bg-gradient-to-b from-gray-900 via-emerald-950 to-gray-900 py-16 sm:py-20 lg:py-24"
    >
      {/* Background */}
      <div className="pointer-events-none absolute inset-0 opacity-10 hidden sm:block">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#059669_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      <motion.div
        className="pointer-events-none absolute right-0 top-0 size-96 rounded-full bg-emerald-500/20 blur-3xl hidden lg:block"
        animate={{ scale: [1, 1.3, 1], x: [0, -50, 0] }}
        transition={{ repeat: Infinity, duration: 12, ease: "easeInOut" }}
      />

      <div className="relative z-10">
        {/* Header (UNCHANGED) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-10 px-4 sm:px-6 text-center lg:px-12"
        >
          <motion.span className="inline-block rounded-full border-2 border-emerald-400/50 bg-emerald-500/20 px-6 py-2 text-sm font-bold text-emerald-300 backdrop-blur">
            📸 Gallery
          </motion.span>

          <h2 className="mt-6 text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black text-white">
           Modern Care in {" "}
            <span className="bg-gradient-to-r from-emerald-400 to-cyan-300 bg-clip-text text-transparent">
              Every Corner
            </span>
          </h2>
        </motion.div>

        {/* ================= MOBILE & TABLET SWIPER ================= */}
        <div className="block lg:hidden px-4">
          <Swiper
            modules={[Pagination]}
            pagination={{ clickable: true }}
            spaceBetween={16}
            slidesPerView={1}
            breakpoints={{
              640: { slidesPerView: 1.2 },
              768: { slidesPerView: 2 },
            }}
            className="pb-10"
          >
            {IMAGES.map((image) => (
              <SwiperSlide key={image.id}>
                <div className="relative h-[320px] sm:h-[360px] overflow-hidden rounded-3xl border-2 border-emerald-500/30 bg-black">
                  <img
                    src={image.url}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* ================= DESKTOP GALLERY (UNCHANGED) ================= */}
        <div className="relative hidden lg:block">
          <div
            ref={scrollRef}
            className="scrollbar-hide flex gap-8 overflow-x-auto px-6 pb-6 pt-8 lg:px-12"
          >
            {IMAGES.map((image, index) => {
              const isHovered = hoveredId === image.id;
              const isActive = index === currentIndex;

              return (
                <motion.div
                  key={image.id}
                  className="group relative flex-shrink-0"
                  style={{ width: `${CARD_WIDTH}px` }}
                  onMouseEnter={() => setHoveredId(image.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  <motion.div
                    className="relative h-[450px] overflow-hidden rounded-3xl"
                    whileHover={{ y: -10 }}
                    style={{
                      border:
                        isHovered || isActive
                          ? `3px solid ${GREEN}`
                          : "2px solid rgba(255,255,255,0.1)",
                      boxShadow:
                        isHovered || isActive
                          ? `0 20px 60px ${GREEN}50, 0 0 40px ${GREEN}30`
                          : "0 10px 40px rgba(0,0,0,0.6)",
                    }}
                  >
                    <motion.img
                      src={image.url}
                      alt=""
                      className="h-full w-full object-cover"
                      animate={isHovered ? { scale: 1.1 } : { scale: 1 }}
                    />
                  </motion.div>
                </motion.div>
              );
            })}
          </div>

          {/* Arrows */}
          <motion.button
            onClick={prev}
            className="absolute left-4 top-1/2 z-20 grid size-16 -translate-y-1/2 place-items-center rounded-full border-2 border-emerald-500/60 bg-emerald-900/90 text-white"
          >
            <ChevronLeft className="size-8" />
          </motion.button>

          <motion.button
            onClick={next}
            className="absolute right-4 top-1/2 z-20 grid size-16 -translate-y-1/2 place-items-center rounded-full border-2 border-emerald-500/60 bg-emerald-900/90 text-white"
          >
            <ChevronRight className="size-8" />
          </motion.button>
        </div>

        {/* Pagination dots (desktop) */}
        <div className="mt-8 hidden lg:flex justify-center gap-2">
          {IMAGES.map((img, i) => (
            <button
              key={img.id}
              onClick={() => scrollTo(i)}
              className="rounded-full transition-all"
              style={{
                backgroundColor: i === currentIndex ? GREEN : "#2e3b2c",
                width: i === currentIndex ? "32px" : "10px",
                height: "10px",
              }}
            />
          ))}
        </div>
      </div>

      <style jsx global>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
}
